import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type SessionBundle } from '../api';
import type { Resumed } from '../engine/keeper';
import { levelPlace, track } from '../lib/events';
import { setPresenceLevel } from '../lib/live-presence';
import { getLocale, onLevelsLocalised } from '../lib/locale';
import {
  askMentorModel,
  conversationState,
  historyFor,
  newConversation,
  saidText,
  type MentorAsk,
  type MentorConversation,
} from '../lib/mentor';
import { flushEditor } from '../lib/editor-flush';
import { readTerminal } from '../lib/terminal-tail';
import { EMPTY_GIT_STATE, type GitState, type GradeResult, type Hint, type PublicMission, type SessionSummary } from '../types';

/**
 * One learner working one level.
 *
 * Owns the session: creating or restoring it, the git state the terminal
 * pushes after every command, grading, hints and reset. Everything the play
 * screen renders comes from here, so the screen itself stays a view.
 *
 * Help is two things and only one of them is in this file's gift. The hint
 * ladder is `askHint`: local, instant, and priced in stars. `askModel` is the
 * other — one HTTP turn against the Worker, which needs an account and spends
 * an allowance, and which can come back with no answer at all. There is no
 * third thing behind it any more. The deterministic cue mentor that used to
 * answer every press for free, and catch every refused turn, has been retired;
 * what is left of it here is `situation`, which names the wrong state the
 * workspace is in without saying a word about it (see below).
 */

const storageKey = (missionId: string) => `engineeros.session.${missionId}`;

/** A failure, stamped so two identical ones in a row are still two events. */
export interface SessionError {
  message: string;
  at: number;
}

export interface SessionApi {
  loading: boolean;
  error: SessionError | null;
  mission: PublicMission | null;
  session: SessionSummary | null;
  git: GitState;
  grade: GradeResult | null;
  hints: Hint[];
  /**
   * Every situation this attempt has been *recognized* as being in, by id.
   *
   * One source now, and it is the silent one: the match the level screen pays
   * for when a learner has stopped converging. Nothing is ever said about a
   * match — the panel that used to say it is gone — so this is a list of what
   * was true of the workspace rather than of what anybody was told, which is
   * exactly what `free_through` wants (docs/mentor.md). The argument for
   * unlocking a rung is that the state itself demonstrates what the rung would
   * have said, and that is as true of a learner nobody spoke to as of one who
   * pressed something.
   */
  situationsSeen: string[];
  reflection: string | null;
  /**
   * The conversation with the mentor, on this level, in this tab.
   *
   * State and not storage. It is posted on every turn and written nowhere
   * else — no `localStorage`, no `sessionStorage`, no request that outlives
   * the turn — which is what makes docs/mentor.md's "no message text is
   * stored anywhere" true of the tab as well as of the Worker. The cost is
   * that a reload starts a fresh one, and the panel says so rather than
   * treating it as a loss.
   *
   * It belongs to a level *and* to a workspace: the id is new for each level
   * and new after a reset, because a conversation carried across a reset is a
   * conversation about a tree that no longer exists.
   */
  conversation: MentorConversation;
  checking: boolean;
  hinting: boolean;
  /** A turn at the online mentor is in flight. */
  asking: boolean;
  /** Bumped after a reset so the terminal reconnects to the rebuilt workspace. */
  reconnectKey: number;
  /** Whether this open brought the learner's unfinished work back, or could not because the level changed. */
  resumed: Resumed;
  /** Open the level again after a failure that left no session to work in. */
  retry(): void;
  /** Applied from the terminal after every command. */
  setGit(state: GitState): void;
  refreshGit(): Promise<void>;
  check(): Promise<GradeResult | null>;
  askHint(): Promise<Hint[] | null>;
  /**
   * Which named wrong state the workspace is in, or null.
   *
   * Expensive — it runs every recognizer the level authored — so the caller
   * is expected to ask rarely and never on the common path. It never rejects:
   * a workspace that cannot answer has not matched anything, which is the same
   * answer the engine gives for a recognizer that throws.
   *
   * The answer is kept as well as returned. A matched id is the most specific
   * true thing the tab knows about this workspace, and it rides on the next
   * turn's context as prompt material — nothing shows it to the learner.
   */
  situation(): Promise<string | null>;
  /**
   * One turn at the online mentor, in the learner's own words or as a choice.
   *
   * Never rejects and never sets `error`: every failure is a value the caller
   * switches on, because the caller's job is to say which fact stopped the
   * turn, and a toast about a request would say none of them.
   */
  askModel(message?: string, choice?: { situation: string; index: number }): Promise<MentorAsk>;
  reset(): Promise<boolean>;
}

export function useSession(missionId: string): SessionApi {
  const [loading, setLoading] = useState(true);
  // Carries a timestamp so that the SAME failure twice in a row is still two
  // distinct values. A bare string would compare equal, React would bail out
  // of the update, and the second attempt would report nothing at all.
  const [error, setError] = useState<SessionError | null>(null);
  const [mission, setMission] = useState<PublicMission | null>(null);
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [git, setGit] = useState<GitState>(EMPTY_GIT_STATE);
  const [grade, setGrade] = useState<GradeResult | null>(null);
  const [hints, setHints] = useState<Hint[]>([]);
  const [situationsSeen, setSituationsSeen] = useState<string[]>([]);
  const [reflection, setReflection] = useState<string | null>(null);
  /** What this open found of the learner's unfinished work (engine/keeper.ts), for the screen to say once. */
  const [resumed, setResumed] = useState<Resumed>(null);
  // Minted lazily so that one id is made per mount rather than one per
  // render, and replaced outright — id and transcript together — whenever the
  // thing being talked about changes underneath it.
  const [conversation, setConversation] = useState<MentorConversation>(newConversation);
  const [checking, setChecking] = useState(false);
  const [hinting, setHinting] = useState(false);
  const [asking, setAsking] = useState(false);
  // The situation matched for the workspace as it stands, or null when the
  // last match found nothing. A ref rather than state: nothing renders it, it
  // is read at the moment a turn is posted, and a re-render per match would be
  // a re-render nobody asked for. `situationsSeen` is the cumulative list and
  // answers a different question — what was ever true — where this answers
  // what is true now, which is the only one a prompt should be given.
  const matched = useRef<string | null>(null);
  const [reconnectKey, setReconnectKey] = useState(0);
  // Every failed start can be tried again, and a retry is a genuinely new
  // attempt: the VM may have finished booting since, or the network may have
  // come back. Counting them is what lets the effect run a second time for a
  // level it has already run for.
  const [attempt, setAttempt] = useState(0);

  // Guards a second create when React re-runs effects in development.
  const started = useRef<string | null>(null);
  const sessionId = session?.id ?? null;

  useEffect(() => {
    const key = `${missionId}#${attempt}`;
    if (started.current === key) return;
    started.current = key;
    let cancelled = false;

    void (async () => {
      setLoading(true);
      setError(null);
      // Everything below describes one workspace and one level, so none of it
      // survives into the next. Cleared here rather than in an effect of its
      // own because the screen is not remounted between missions (browser.md,
      // decision 5) and a stale match would ride on the first turn of a level
      // it was never true of.
      setSituationsSeen([]);
      setResumed(null);
      matched.current = null;
      // A new level is a new conversation, id and all. The screen is not
      // remounted between missions, so without this the transcript from level
      // 12 would be posted as context for a question about level 13 — and the
      // conversation's own limits would already be half spent on arrival.
      setConversation(newConversation());
      try {
        const stored = (() => {
          try {
            return sessionStorage.getItem(storageKey(missionId));
          } catch {
            return null;
          }
        })();

        let bundle: SessionBundle | null = null;
        if (stored) {
          try {
            bundle = await api.getSession(stored);
          } catch (err) {
            // A session the runtime has never heard of is gone for good: the
            // tab was reloaded, or another level took the one workspace with
            // it. Starting the level again is all that is left. Any other
            // failure — the VM did not boot, a command timed out — may leave
            // that session very much alive, and creating a second one would
            // reseed the workspace the learner is halfway through. Report
            // those instead of quietly throwing the work away.
            if ((err as { status?: number }).status !== 404) throw err;
            try {
              sessionStorage.removeItem(storageKey(missionId));
            } catch {
              /* storage unavailable */
            }
          }
        }
        if (!bundle) {
          // What the learner waits through, measured around the one call that
          // makes them wait. Monotonic, so a clock adjustment mid-open cannot
          // produce a negative number or a three-hour one — the same reason
          // the boot measures itself this way (lib/events.ts).
          const openedAt = performance.now();
          bundle = await api.createSession(missionId);
          const openMs = Math.round(performance.now() - openedAt);
          // Counted here rather than beside the restore above, which is the
          // distinction the funnel rests on: a new session is somebody
          // arriving at a level, and a restored one is the same person coming
          // back to the level they were already on. Counting both would make
          // every level look busier the more people got stuck on it
          // (docs/analytics.md).
          //
          // The track travels with the number because the number is a position
          // within a track and not a position in the course: once Act III
          // branches, every specialization has a level 0 of its own
          // (docs/curriculum.md §5), and a funnel that grouped on the level
          // alone would pile them into one arrival curve nobody walked. Both
          // are read off the bundle that has just arrived — the mission is the
          // only thing that knows which course this is, and it is asked again
          // for every level rather than remembered.
          //
          // `value` is how long that took. The boot has been measured since
          // the day it shipped and opening a level never was, although it is
          // the wait a learner sits through on every level rather than once a
          // visit — and it is the number that says whether the machine is
          // slow for everybody or only on the hardware nobody here owns.
          track('level-start', { level: bundle.mission.level, track: bundle.mission.track, value: openMs });
          try {
            sessionStorage.setItem(storageKey(missionId), bundle.session.id);
          } catch {
            /* storage unavailable */
          }
        }
        if (cancelled) return;
        setResumed(bundle.resumed ?? null);
        setMission(bundle.mission);
        setSession(bundle.session);
        setGit(bundle.state);
        setGrade(bundle.lastGrade);
        setHints(bundle.hints);
        setReflection(bundle.reflection);
      } catch (err) {
        if (!cancelled) setError({ message: (err as Error).message, at: Date.now() });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [missionId, attempt]);

  // Live "online now" counts by level: set while a level is open, cleared on
  // leaving it. Restored sessions count too, unlike `level-start`, because
  // this is where somebody is rather than where they arrived.
  const openLevel = mission?.level;
  const openTrack = mission?.track;
  useEffect(() => {
    if (openLevel === undefined || !openTrack) return;
    setPresenceLevel({ level: openLevel, track: openTrack });
    // And where the tab is, so the time spent in this level is counted
    // (lib/events.ts, `leave`).
    levelPlace({ level: openLevel, track: openTrack });
    return () => {
      setPresenceLevel(null);
      levelPlace(null);
    };
  }, [openLevel, openTrack]);

  const retry = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  /**
   * Follow a language switch without reopening the level.
   *
   * The mission, the rungs already bought and the reflection were copied out
   * of the runtime when the level opened, and a switch rewrites the runtime's
   * mission in place (engine/runtime.ts, `applyLocale`) — so the copies are
   * re-read once the new words have landed. Until this, the chrome turned
   * Arabic around a brief that stayed English, and the only way to get the
   * brief across was a reload: a rebooted machine and a reseeded workspace.
   *
   * On the words alone. The workspace, the terminal and the conversation are
   * the same level in either language and nothing about them moves. A
   * failure keeps the words on screen, which are still this level's, in the
   * language the learner was reading a moment ago.
   */
  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    const stop = onLevelsLocalised(() => {
      void api.sessionText(sessionId).then(
        (words) => {
          if (!live) return;
          setMission(words.mission);
          setHints(words.hints);
          setReflection(words.reflection);
        },
        () => {
          /* the words already on screen are still this level's */
        },
      );
    });
    return () => {
      live = false;
      stop();
    };
  }, [sessionId]);

  const refreshGit = useCallback(async () => {
    if (!sessionId) return;
    try {
      setGit(await api.getState(sessionId));
    } catch {
      /* the terminal will push a fresh state after the next command */
    }
  }, [sessionId]);

  const check = useCallback(async (): Promise<GradeResult | null> => {
    if (!sessionId) return null;
    setChecking(true);
    try {
      // What is on the learner's screen, not what was last saved: the grade
      // reads the disk, and the editor's buffer goes there first
      // (lib/editor-flush.ts).
      await flushEditor(sessionId);
      const r = await api.check(sessionId);
      setGrade(r.result);
      setSession(r.session);
      setReflection(r.reflection);
      return r.result;
    } catch (err) {
      setError({ message: (err as Error).message, at: Date.now() });
      return null;
    } finally {
      setChecking(false);
    }
  }, [sessionId]);

  const askHint = useCallback(async (): Promise<Hint[] | null> => {
    if (!sessionId) return null;
    setHinting(true);
    try {
      const r = await api.hint(sessionId);
      setHints(r.hints);
      return r.hints;
    } catch (err) {
      setError({ message: (err as Error).message, at: Date.now() });
      return null;
    } finally {
      setHinting(false);
    }
  }, [sessionId]);

  /**
   * The workspace was in this named wrong state.
   *
   * Idempotent, because the same state can be matched again on the next check
   * that fails the same way: what `free_through` wants to know is whether a
   * state was ever true of this attempt, not how often.
   */
  const noteRecognized = useCallback((id: string) => {
    setSituationsSeen((seen) => (seen.includes(id) ? seen : [...seen, id]));
  }, []);

  /**
   * Which named wrong state the workspace is in.
   *
   * No busy flag and no `setError`, and both absences are the point: nothing
   * on screen waits for this, and nothing on screen changes when it answers. It
   * is asked on the learner's behalf, so a learner who is mid-level must never
   * be shown a spinner — or an apology — for a question they did not ask.
   */
  const situation = useCallback(async (): Promise<string | null> => {
    if (!sessionId) return null;
    try {
      const { situation: id } = await api.situation(sessionId);
      // Two readers, and they want different halves of this. `free_through`
      // wants "was this ever true", because a state that proves what the first
      // rungs would have said proves it whether or not it is still there; the
      // model wants "is this true now", because a prompt describing a mistake
      // the learner has already climbed out of is worse than one describing
      // nothing. So null is written through to `matched` and is not written to
      // the list.
      matched.current = id;
      if (id) noteRecognized(id);
      return id;
    } catch {
      // A workspace that could not answer has not matched anything. The same
      // answer the engine gives for a recognizer it cannot run — and the same
      // answer for what rides on the next turn, since nothing was established.
      matched.current = null;
      return null;
    }
  }, [sessionId, noteRecognized]);

  /**
   * One turn at the online mentor.
   *
   * The context is built fresh for every turn rather than kept in state: it is
   * a description of the workspace, the workspace moves under it constantly,
   * and a turn posted against a minute-old tree is the stale-grade problem
   * again with a bill attached. The matched situation goes in with it: it is
   * the one thing the rules established about *this* tree rather than about
   * the level, and it is prompt material now rather than a signal to nudge
   * anybody. It is handed over as a claim about the last Check, not as the
   * answer: once the tree has moved since that Check, `mentorContext` drops it
   * and looks again.
   *
   * There is no `setError` anywhere in here. Every failure is in the return
   * value, and the composer's answer to each of them is a different sentence.
   * A toast saying a request failed would be the product apologising for its
   * own plumbing at the moment somebody asked for help, and it would say the
   * same thing about six facts that are not the same.
   *
   * ## What the transcript records, and what it does not
   *
   * Only exchanges that happened. A question that came back refused, held,
   * quota'd or lost on the wire adds nothing to it and is left in the field
   * for another go — so the transcript is a record of what was said rather
   * than a log of what was attempted, and the history posted next turn cannot
   * contain a question the model was never shown.
   *
   * The two conversation refusals are the exception to the "leave it in the
   * field" half, and unavoidably so: they close the composer, so there is no
   * field left to leave it in. What the learner reads instead is the line
   * naming which limit it was.
   *
   * ## The two limits are read back, never counted here
   *
   * Every answer and every conversation refusal carries `{ spent, budget,
   * resetAt, messages, cap }`, and it is kept exactly as it arrived. The tab
   * counted its own once and the count was wrong in a way the screen would
   * not have shown: the disclosure budget is per account per level per
   * rolling day, so a reload starts a transcript at zero while the day's
   * spend carries on.
   *
   * Keeping the Worker's own numbers also closes the composer *without
   * spending a turn to be told* — the block that arrives with the message
   * that used the last one already says it was the last one.
   */
  const askModel = useCallback(async (
    message?: string,
    choice?: { situation: string; index: number },
  ): Promise<MentorAsk> => {
    if (!sessionId || !mission) return { outcome: 'error' };
    setAsking(true);
    try {
      // The code on the screen, not the code last saved, for the reason Check
      // flushes (lib/editor-flush.ts): a learner who fixed the file in the
      // editor and asked why it still fails was otherwise answered about the
      // version before the fix — the mentor diagnosing a line that is no
      // longer there. The write records a save, so the grade taken for this
      // question is a fresh one (`checkIsCurrent`).
      await flushEditor(sessionId);
      // The screen as it is at the moment of asking. Read here, before the
      // context is built, because building it can take seconds of grading
      // and the question is about what the learner was looking at when they
      // asked it.
      const terminal = readTerminal(sessionId);
      // A picked choice's own state and the state the last Check matched, kept
      // apart: the first is what the question is about, the second is true
      // only while that Check is still the grade, and `mentorContext` is what
      // knows whether it is (api.ts).
      const context = await api.mentorContext(sessionId, {
        ...(choice ? { choice: choice.situation } : {}),
        matched: matched.current,
        ...(terminal ? { terminal } : {}),
      });
      // The values this learner has to find and has not been shown, for the
      // Worker's filter and never for the model — which is why they travel
      // beside the context rather than in it. A failure here fails the turn
      // like one of `mentorContext`'s would: asking without them would send
      // the one question the filter could not protect.
      const hidden = await api.mentorHidden(sessionId);
      // Read at the moment the turn is posted rather than closed over, so
      // that a second press cannot post the transcript as it was before the
      // first one landed. The composer holds one turn at a time, which makes
      // this belt and braces — and the braces are cheap.
      const posted = conversation;
      const result = await askMentorModel({
        missionId: mission.id,
        lang: getLocale(),
        context,
        hidden,
        conversationId: posted.id,
        history: historyFor(posted.turns),
        ...(message ? { message } : {}),
        ...(choice ? { choice } : {}),
      });
      setConversation((current) => {
        // The level changed, or the workspace was reset, while this turn was
        // in flight. The answer is about a conversation that no longer
        // exists; dropping it is the same judgement `matched` makes.
        if (current.id !== posted.id) return current;
        // An id the Worker says is not this account's, or is this account's
        // on another level. Unreachable through the interface, so it is a
        // stale or forged id rather than something the learner did — and a
        // dead panel would be a punishment for neither. A fresh conversation
        // is the answer, and the transcript goes with the id, because what
        // the Worker has just refused to continue is the thing it belonged
        // to. The composer stays open; the panel says what happened.
        if (result.outcome === 'not-yours') return newConversation();
        const spend = 'conversation' in result ? result.conversation ?? current.spend : current.spend;
        // Both limits, as the Worker counts them, after this message. A
        // conversation that has reached either is over whether or not this
        // particular message was the one refused for it — which is what
        // saves the learner a turn spent finding out.
        const ended = result.outcome === 'spent' || result.outcome === 'capped'
          ? result.outcome
          : current.ended ?? conversationState(spend).atLimit;
        if (result.outcome === 'answered' || result.outcome === 'cached') {
          return {
            ...current,
            spend,
            ended,
            turns: [
              ...current.turns,
              ...(message ? [{ role: 'learner' as const, text: message }] : []),
              { role: 'mentor' as const, text: saidText(result.reply), reply: result.reply },
            ],
          };
        }
        return { ...current, spend, ended };
      });
      return result;
    } catch {
      // `mentorContext` reads the workspace, so it can fail the way every
      // other call here can, and before anything has been asked of anybody.
      // The composer reads it as the turn that went nowhere, which it is.
      return { outcome: 'error' };
    } finally {
      setAsking(false);
    }
  }, [sessionId, mission, conversation]);

  const reset = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    try {
      const r = await api.reset(sessionId);
      setSession(r.session);
      setGit(r.state);
      setGrade(null);
      setReflection(null);
      // The tree the recognizer matched has just been rebuilt, so the match is
      // a statement about a workspace that no longer exists and must not ride
      // on the next turn. `situationsSeen` is not cleared with it: a reset is
      // part of the attempt, and a state the learner got into before they threw
      // the workspace away is still a state they got into — which is the whole
      // of what `free_through` is priced on.
      matched.current = null;
      // The conversation goes with the tree it was about. Every sentence in
      // it describes files, refs and a mistake that have just been deleted,
      // so carrying it over would mean the next question is answered against
      // a workspace nobody is looking at. A new id, not a cleared list: the
      // two halves have to agree about which conversation this is, and the id
      // is the only thing that says so.
      //
      // It is also a fresh message cap, which is the one limit counted per
      // conversation. It is NOT a fresh disclosure budget: that is counted per
      // account, level and rolling day in the Worker, so a reset cannot buy
      // more of it — and it costs a whole workspace besides.
      setConversation(newConversation());
      setReconnectKey((n) => n + 1);
      // A reset is the learner throwing their work away and starting the
      // level again. Productive failure is a design principle here, so this
      // is not a number to drive down — but a level whose reset count is
      // three times its neighbours' is a level whose brief is unclear, and
      // that is not visible in clears or in stars.
      track('level-reset', { level: mission?.level, track: mission?.track });
      return true;
    } catch (err) {
      setError({ message: (err as Error).message, at: Date.now() });
      return false;
    }
    // `mission?.level` and `mission?.track` and not `mission`: the object is
    // replaced on every grade, and rebuilding this callback that often would
    // remake the terminal's reset handler for two values that have not
    // changed. Both are optional-chained for the same reason the level always
    // was — a reset can be attempted before the mission has arrived, and a
    // reset with nothing to reset genuinely has no level and no track to name,
    // which the Worker stores as NULL rather than refusing (docs/analytics.md).
    //
    // Read from the mission on every send, and held nowhere that outlives it.
    // A track is what this level is, not what this learner is: the hook is
    // rebuilt for each mission, so the value cannot follow a learner who
    // switches tracks between one level and the next.
  }, [sessionId, mission?.level, mission?.track]);

  return {
    loading,
    error,
    mission,
    session,
    git,
    grade,
    hints,
    situationsSeen,
    reflection,
    conversation,
    checking,
    hinting,
    asking,
    reconnectKey,
    resumed,
    retry,
    setGit,
    refreshGit,
    check,
    askHint,
    situation,
    askModel,
    reset,
  };
}
